import React, { useEffect, useId, useRef, useState } from 'react';
import { MAX_FILTER_TAGS, tagChoices, toggleTag } from '../utils/tagSelect';

// A Dashboard filter-rail tag picker (Tag / Exclude Tag): a button saying how
// many tags are picked opens a panel with a search box and a checkbox per
// inventory tag; the picked tags show under the button as chips (coloured
// like the table's tag chips), each a button that removes it. A click
// outside, Esc (focus back on the button) or tabbing out closes the panel.

const NO_TAGS = [];
const NO_TAG_COLORS = {};

/**
 * @param {string} props.label  what the picker filters ("Tag", "Exclude Tag"), for screen readers
 * @param {string[]} props.options  useInventoryTags data
 * @param {string[]} props.selected  the picked tags
 * @param {Function} props.onChange  (nextSelected) => void, always a new array
 * @param {object} [props.tagColors]  useTagColors data: { tag: color }
 * @param {string} [props.placeholder]  the button text while nothing is picked
 */
function TagMultiSelect({ label, options, selected, onChange, tagColors = NO_TAG_COLORS, placeholder = 'Any tag' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const menuId = useId();

  const list = Array.isArray(selected) ? selected : NO_TAGS;
  const choices = open ? tagChoices(options, list, query) : NO_TAGS;
  const isFull = list.length >= MAX_FILTER_TAGS;

  // The cursor goes to the search box when the panel opens
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // A click outside closes it
  useEffect(() => {
    if (!open) return undefined;
    const handleDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [open]);

  const handleToggleOpen = () => {
    if (!open) setQuery('');
    setOpen(o => !o);
  };
  const handleKeyDown = (e) => {
    if (e.key !== 'Escape' || !open) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    buttonRef.current?.focus();
  };
  // Focus moving out (Tab) closes it; a click inside on no control keeps it open
  const handleBlur = (e) => {
    if (open && e.relatedTarget && wrapRef.current && !wrapRef.current.contains(e.relatedTarget)) setOpen(false);
  };

  const emptyText = query.trim() ? `No tag matches “${query.trim()}”.` : 'No tags in inventory.';

  return (
    <div className="tag-multi" ref={wrapRef} onKeyDown={handleKeyDown} onBlur={handleBlur}>
      <div className="tag-multi-anchor">
        <button
          type="button"
          ref={buttonRef}
          className={`filter-input tag-multi-toggle${list.length ? ' on' : ''}`}
          aria-label={list.length ? `${label}: ${list.join(', ')}` : `${label}: none picked`}
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={handleToggleOpen}
        >
          <span className="tag-multi-toggle-text">{list.length ? `${list.length} selected` : placeholder}</span>
          <span className="tag-multi-caret" aria-hidden="true">▾</span>
        </button>
        {open && (
          <div className="tag-multi-menu" id={menuId} role="group" aria-label={label}>
            <input
              ref={searchRef}
              type="text"
              className="filter-input"
              value={query}
              placeholder="Search tags..."
              aria-label={`Search tags for ${label}`}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="tag-multi-list">
              {choices.map(c => {
                const isDisabled = !c.isSelected && isFull;
                return (
                  <label
                    key={c.tag}
                    className={`tag-multi-item${isDisabled ? ' disabled' : ''}`}
                    title={isDisabled ? `${MAX_FILTER_TAGS} tags at most` : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={c.isSelected}
                      disabled={isDisabled}
                      onChange={() => onChange(toggleTag(list, c.tag))}
                    />
                    <span className="tag-multi-name">{c.tag}</span>
                  </label>
                );
              })}
              {choices.length === 0 && <div className="tag-multi-empty">{emptyText}</div>}
            </div>
          </div>
        )}
      </div>
      {list.length > 0 && (
        <div className="tag-multi-chips">
          {list.map(tag => {
            const color = tagColors[tag];
            return (
              <button
                key={tag}
                type="button"
                className="tag-chip tag-multi-chip"
                style={color ? { backgroundColor: color, color: '#fff', borderColor: color } : undefined}
                title={`Remove ${tag}`}
                aria-label={`Remove ${tag} from ${label}`}
                onClick={() => onChange(toggleTag(list, tag))}
              >
                {tag}<span className="tag-multi-x" aria-hidden="true">×</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TagMultiSelect;
